import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AUTH } from '@/constants/testIds';
import { ArrowLeft, ShieldAlert, Clock } from 'lucide-react';

export default function Login() {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-stretch bg-background">
      <div className="flex-1 hidden lg:flex items-center justify-center relative overflow-hidden border-r border-border">
        <div className="absolute inset-0 grid-skeleton opacity-40" />
        <div className="relative z-10 max-w-md px-10">
          <div className="font-display text-5xl leading-tight tracking-tight">
            Engineer authority. <span className="text-primary">Compound it.</span>
          </div>
          <p className="mt-6 text-muted-foreground">
            AuthorityOS™ measures, improves and grows brand authority across AI assistants, search engines and knowledge graphs.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-3">
            {['Orchestration', 'Reasoning', '29 Agents'].map(k => (
              <div key={k} className="rounded-md border border-border bg-card px-3 py-2 text-xs text-center text-muted-foreground">{k}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" data-testid={AUTH.loginBackLink} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
          <div className="mt-10">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-display">A</span>
              </div>
              <span className="font-display text-xl">AuthorityOS™</span>
            </div>

            {status === 'pending' && (
              <div data-testid="auth-pending-banner" className="mt-8 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
                <div className="flex items-center gap-2 font-medium text-amber-700 dark:text-amber-400">
                  <Clock className="h-4 w-4" /> Access request submitted
                </div>
                <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                  AuthorityOS™ is invite-only. Your sign-in has been logged and sent to the administrators
                  for approval. You'll be able to sign in as soon as access is approved.
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="mt-8 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
                <div className="flex items-center gap-2 font-medium text-destructive">
                  <ShieldAlert className="h-4 w-4" /> Sign-in failed
                </div>
                <p className="mt-1 text-muted-foreground text-xs">Please try again, or contact your administrator.</p>
              </div>
            )}

            <h1 className="mt-8 font-display text-3xl tracking-tight">Welcome back.</h1>
            <p className="mt-2 text-sm text-muted-foreground">Sign in to your AI authority command center.</p>

            <Button
              data-testid={AUTH.googleLogin}
              onClick={handleGoogleLogin}
              size="lg"
              className="mt-8 w-full h-12 text-base"
            >
              <GoogleIcon /> Continue with Google
            </Button>

            <div className="mt-6 rounded-md bg-secondary/40 p-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Invite-only access.</span> Sign in with the
              same email an administrator has approved for you. New requests are reviewed by the admin team.
            </div>

            <p className="mt-6 text-xs text-muted-foreground">
              By continuing, you agree to AuthorityOS™ Terms and acknowledge our Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="mr-2" width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#fff" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.86 2.69-6.62z"/>
      <path fill="#fff" d="M9 18c2.43 0 4.46-.8 5.95-2.18l-2.9-2.26c-.8.54-1.83.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A8.99 8.99 0 0 0 9 18z"/>
      <path fill="#fff" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.96H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3.01-2.32z"/>
      <path fill="#fff" d="M9 3.58c1.32 0 2.5.45 3.44 1.34l2.58-2.58A8.99 8.99 0 0 0 9 0 8.99 8.99 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z"/>
    </svg>
  );
}
